import React, { useEffect, useMemo, useState } from "react";
import { fetchDemandForecast } from "../api/demandForecastService";

import {
    PageHeader,
    Panel,
    KpiCard,
} from "./ui/AdminUI";

import DataTable from "./ui/DataTable";
import { DarkButton } from "./ui/FormControls";

const normalizeForecastData = (data) => {
    const raw = Array.isArray(data)
        ? data
        : Array.isArray(data?.predictions)
            ? data.predictions
            : [];

    return raw.map((item, index) =>
        typeof item === "number"
            ? {
                id: index + 1,
                forecastDate: new Date(
                    Date.now() + index * 24 * 60 * 60 * 1000
                ).toISOString(),
                shipmentCount: Math.round(item),
            }
            : {
                id: item.id ?? index + 1,
                forecastDate:
                    item.forecastDate ??
                    new Date(Date.now() + index * 24 * 60 * 60 * 1000).toISOString(),
                shipmentCount: Math.round(
                    item.shipmentCount ?? item.predictedShipments ?? 0
                ),
            }
    );
};

const DemandForecast = () => {
    const [forecasts, setForecasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadForecast = async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await fetchDemandForecast();
            setForecasts(normalizeForecastData(data));
        } catch (err) {
            console.error("Failed to load forecast data:", err);
            setError(
                err?.response?.data?.message ||
                err.message ||
                "Failed to load forecast data."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadForecast();
    }, []);

    const stats = useMemo(() => {
        const total = forecasts.reduce(
            (sum, x) => sum + Number(x.shipmentCount || 0),
            0
        );

        const max = forecasts.reduce(
            (m, x) => Math.max(m, Number(x.shipmentCount || 0)),
            0
        );

        const avg =
            forecasts.length > 0
                ? Math.round(total / forecasts.length)
                : 0;

        return { total, max, avg };
    }, [forecasts]);

    const columns = [
        {
            key: "forecastDate",
            label: "Date",
            render: (row) =>
                row.forecastDate
                    ? new Date(row.forecastDate).toLocaleDateString()
                    : "—",
        },
        {
            key: "shipmentCount",
            label: "Shipment Count",
            render: (row) => (
                <span className="font-bold text-blue-700">
                    {row.shipmentCount}
                </span>
            ),
        },
    ];

    if (loading) {
        return (
            <div className="p-6 text-slate-600">
                Loading demand forecast...
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Demand Forecast"
                subtitle="AI-based shipment demand prediction for future logistics planning."
                action={
                    <DarkButton onClick={loadForecast}>
                        Refresh Forecast
                    </DarkButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <KpiCard title="Forecast Days" value={forecasts.length} color="blue" />
                <KpiCard title="Total Demand" value={stats.total} color="green" />
                <KpiCard title="Peak Day" value={stats.max} color="purple" />
            </div>

            <Panel
                title="Forecast Table"
                subtitle="Predicted shipment volume by date."
            >
                <DataTable
                    columns={columns}
                    rows={forecasts}
                    rowKey="id"
                    emptyText="No forecast data available."
                />
            </Panel>

            <Panel
                title="Forecast Summary"
                subtitle="Aggregated demand forecast indicators."
            >
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-slate-500 text-sm">Average Daily Demand</p>
                        <p className="text-3xl font-black text-slate-900 mt-2">
                            {stats.avg}
                        </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-slate-500 text-sm">Forecast Horizon</p>
                        <p className="text-3xl font-black text-slate-900 mt-2">
                            {forecasts.length} days
                        </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                        <p className="text-slate-500 text-sm">Model Status</p>
                        <p className="text-3xl font-black text-green-700 mt-2">
                            Ready
                        </p>
                    </div>
                </div>
            </Panel>
        </div>
    );
};

export default DemandForecast;